import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { PaymentsStatisticsTotals, PaymentsStatisticsTotal } from 'entities/Warehouse';
import { sortPaymentsStatistics } from '../../utils/sortPaymentsStatistics';
import { PaymentStatisticsCard } from '../PaymentStatisticsCard';

interface PaymentsStatisticsCardsProps {
  data?: PaymentsStatisticsTotals;
  isLoading?: boolean;
}

export const PaymentsStatisticsCards: FC<PaymentsStatisticsCardsProps> = memo((props) => {
  const { data, isLoading } = props;
  const { t } = useAppTranslation('common');

  const diff = useMemo<PaymentsStatisticsTotal[]>(() => {
    if (!data) return [];

    const refundsMap = new Map(data.refunds.map((r) => [r.type, r]));
    const allTypes = new Set([...data.payments.map((p) => p.type), ...data.refunds.map((r) => r.type)]);

    return Array.from(allTypes).map((type) => {
      const payment = data.payments.find((p) => p.type === type);
      const refund = refundsMap.get(type);

      return {
        type,
        amount: (payment?.amount ?? 0) - (refund?.amount ?? 0),
        vatAmount: (payment?.vatAmount ?? 0) - (refund?.vatAmount ?? 0),
      };
    });
  }, [data]);

  if (isLoading || !data) {
    return (
      <div className="bg-secondaryLight rounded-lg p-6 mt-6 animate-pulse">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className="bg-white rounded-lg p-6">
              <div className="h-5 bg-gray-200 rounded mb-3 w-1/3"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondaryLight rounded-lg p-6 mt-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <PaymentStatisticsCard title={t('Payments')} data={sortPaymentsStatistics(data.payments)} />
        <PaymentStatisticsCard title={t('Credit notes')} data={sortPaymentsStatistics(data.credits)} />
        <PaymentStatisticsCard title={t('Refunds')} data={sortPaymentsStatistics(data.refunds)} />
        <PaymentStatisticsCard title={t('Payments/Refunds Diff')} data={sortPaymentsStatistics(diff)} isDiff />
      </div>
    </div>
  );
});
